// =============================================================================
// FILE: packages/renderer/src/lib/institutional.ts
// PURPOSE: Client for the R2 Institutional Licensing & Financing framework —
//   public deployment packages + discount programs, government proposal
//   requests, and the admin Quote Generator + proposal approval workflow.
// =============================================================================

const FOUNDER_TOKEN_LS = 'rcd2.founder.token';

export interface InstitutionalPackage {
  id: string; name: string; purpose: string;
  setup: number | null; monthly: number | null; users: number | null;
  custom: boolean; features: string[]; contact?: string;
}
export interface DiscountProgram { id: string; name: string; pct: number; desc: string; }

export interface Proposal {
  id: string; organization: string; org_type?: string | null; contact_name?: string | null; email?: string | null;
  package_id: string; package_name: string; users?: number | null;
  discount_id: string; discount_pct: number; term_months: number; timeline_weeks?: number | null;
  exec_summary?: string | null; needs?: string | null; features?: string[];
  standard_setup: number | null; standard_monthly: number | null;
  final_setup: number | null; final_monthly: number | null;
  status: 'draft' | 'approved' | 'sent' | 'rejected'; version: number;
  request_id?: string | null;
  approval_history: Array<{ action: string; at: string; by: string }>;
  created_at: string;
}

export interface GovRequest {
  id: string; organization: string; org_type?: string | null; contact_name: string; email: string;
  phone?: string | null; package_interest?: string | null; message?: string | null; status: string; created_at: string;
}

function adminHeaders(adminEmail?: string): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  try { const t = localStorage.getItem(FOUNDER_TOKEN_LS); if (t) h.Authorization = `Bearer ${t}`; } catch { /* */ }
  if (adminEmail) h['X-Admin-Email'] = adminEmail;
  return h;
}

export async function fetchPackages(): Promise<{ packages: InstitutionalPackage[]; discounts: DiscountProgram[] }> {
  const r = await fetch('/api/institutional/packages');
  if (!r.ok) throw new Error('Unavailable');
  return r.json();
}

export async function updatePackage(id: string, patch: Partial<InstitutionalPackage>, adminEmail?: string): Promise<{ package: InstitutionalPackage }> {
  const r = await fetch(`/api/admin/institutional/packages/${id}`, { method: 'PUT', headers: adminHeaders(adminEmail), body: JSON.stringify(patch) });
  if (!r.ok) throw new Error('Update failed');
  return r.json();
}

export async function requestGovProposal(input: {
  organization: string; org_type?: string; contact_name: string; email: string; phone?: string; package_interest?: string; message?: string;
}): Promise<{ ok: boolean; id: string; message: string }> {
  const r = await fetch('/api/gov/proposal-request', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(input) });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || 'Request failed');
  return r.json();
}

export async function fetchGovRequests(adminEmail?: string): Promise<{ requests: GovRequest[]; total: number }> {
  const r = await fetch('/api/admin/gov/proposal-requests', { headers: adminHeaders(adminEmail) });
  if (!r.ok) throw new Error('Unauthorized');
  return r.json();
}

export async function createProposal(input: Record<string, unknown>, adminEmail?: string): Promise<{ proposal: Proposal }> {
  const r = await fetch('/api/admin/proposals', { method: 'POST', headers: adminHeaders(adminEmail), body: JSON.stringify(input) });
  if (!r.ok) throw new Error('Create proposal failed');
  return r.json();
}

export async function fetchProposals(adminEmail?: string): Promise<{ proposals: Proposal[]; counts: Record<string, number>; total: number }> {
  const r = await fetch('/api/admin/proposals', { headers: adminHeaders(adminEmail) });
  if (!r.ok) throw new Error('Unauthorized');
  return r.json();
}

export async function proposalAction(id: string, action: 'approve' | 'send' | 'reject', adminEmail?: string): Promise<void> {
  const r = await fetch(`/api/admin/proposals/${id}/${action}`, { method: 'POST', headers: adminHeaders(adminEmail) });
  if (!r.ok) throw new Error(`${action} failed`);
}

export function money(v: number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}
